//
// Copyright 2020 DXOS.org
//

import '@dxos-theme';

import React, { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { log } from '@dxos/log';
import { createBuf } from '@dxos/protocols/buf';
import { ProfileDocumentSchema } from '@dxos/protocols/buf/dxos/halo/credentials_pb';
// TODO(wittjosiah): Restore observability for testbench.
// import { initializeAppObservability } from '@dxos/observability';
import { type Client, ClientProvider } from '@dxos/react-client';
import * as Theme from '@dxos/react-ui/Theme';
import { Expando } from '@dxos/schema';
import { TRACE_PROCESSOR } from '@dxos/tracing';
import { type ThemeMode } from '@dxos/ui-types';

import { AppContainer, Error, Main } from './components/index.ts';
import { SyncBench } from './components/SyncBench.tsx';
import { getConfig } from './config.ts';
import { Document, Item } from './data.ts';
import { translations } from './translations.ts';

TRACE_PROCESSOR.setInstanceTag('app');

// void initializeAppObservability({
//   namespace: 'testbench.dxos.org',
//   config: new Config(Defaults()),
// });

const router = createBrowserRouter([
  {
    path: '*',
    errorElement: <Error />,
    element: (
      <AppContainer>
        <Main />
      </AppContainer>
    ),
  },
  {
    path: '/sync-bench',
    element: <SyncBench />,
  },
]);

// TODO(burdon): Factor out. See copy paste in devtools.
const useThemeWatcher = () => {
  const [themeMode, setThemeMode] = useState<ThemeMode>('dark');
  const setTheme = ({ matches: prefersDark }: { matches?: boolean }) => {
    document.documentElement.classList[prefersDark ? 'add' : 'remove']('dark');
    setThemeMode(prefersDark ? 'dark' : 'light');
  };

  useEffect(() => {
    const modeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setTheme({ matches: modeQuery.matches });
    modeQuery.addEventListener('change', setTheme);
    return () => modeQuery.removeEventListener('change', setTheme);
  }, []);

  return themeMode;
};

const App = () => {
  const themeMode = useThemeWatcher();
  return (
    <Theme.Provider tx={Theme.defaultTx} themeMode={themeMode} resourceExtensions={translations}>
      <RouterProvider router={router} />
    </Theme.Provider>
  );
};

const main = async () => {
  const config = await getConfig();
  log.config({ filter: config.get('runtime.client.log.filter'), prefix: config.get('runtime.client.log.prefix') });

  const handleInitialized = async (client: Client) => {
    const searchProps = new URLSearchParams(location.search);
    const deviceInvitationCode = searchProps.get('deviceInvitationCode');
    const identity = client.halo.identity.get();
    if (!identity && !deviceInvitationCode) {
      await client.halo.createIdentity(createBuf(ProfileDocumentSchema, { displayName: 'Testbench User' }));
      // TODO(wittjosiah): Ideally this would be per app rather than per identity.
    } else if (deviceInvitationCode) {
      await client.shell.joinIdentity({ invitationCode: deviceInvitationCode }).then(({ identity }) => {
        if (!identity) {
          return;
        }

        const url = new URL(window.location.href);
        const params = Array.from(url.searchParams.entries());
        const [name] = params.find(([_, value]) => value === deviceInvitationCode) ?? [null, null];
        if (name) {
          url.searchParams.delete(name);
          history.replaceState({}, document.title, url.href);
        }
      });
    }

    await client.addTypes([Item, Document]);
  };

  const root = createRoot(document.getElementById('root')!);
  root.render(
    // NOTE: StrictMode will cause the entire stack to render twice.
    <StrictMode>
      <ClientProvider config={config} onInitialized={handleInitialized} types={[Expando.Expando]}>
        <App />
      </ClientProvider>
    </StrictMode>,
  );
};

void main();
